import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogDescription } from "@/components/ui/alert-dialog";
import { brl } from "@/lib/format";
import { Plus, Pencil, Trash2, UserCircle2, Clock, CalendarX, X as XIcon, KeyRound, Scissors } from "lucide-react";
import { can } from "@/lib/permissions";

const DAYS = [
  { key: "1", label: "Segunda" },
  { key: "2", label: "Terça" },
  { key: "3", label: "Quarta" },
  { key: "4", label: "Quinta" },
  { key: "5", label: "Sexta" },
  { key: "6", label: "Sábado" },
  { key: "0", label: "Domingo" },
];
const defaultHours = () => ({
  "0": { start: "09:00", end: "13:00", closed: true },
  "1": { start: "09:00", end: "19:00", closed: false },
  "2": { start: "09:00", end: "19:00", closed: false },
  "3": { start: "09:00", end: "19:00", closed: false },
  "4": { start: "09:00", end: "19:00", closed: false },
  "5": { start: "09:00", end: "19:00", closed: false },
  "6": { start: "09:00", end: "14:00", closed: false },
});
const empty = { name: "", email: "", phone: "", role: "", default_commission_type: "percentage", default_commission_value: 40, working_hours: defaultHours(), time_off: [], active: true, access_email: "", access_password: "", can_view_all_appointments: false, can_view_commission: false, fixed_only: false, fixed_address: "", fixed_radius_km: 0.5, travel_speed_kmh: "", override_commissions: [] };

export default function Professionals() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => setList((await api.get("/professionals")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p, email: p.email || "", working_hours: p.working_hours || defaultHours(), time_off: p.time_off || [], override_commissions: p.override_commissions || [] }); setOpen(true); };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setDay = (day, patch) => setForm((f) => ({ ...f, working_hours: { ...f.working_hours, [day]: { ...f.working_hours[day], ...patch } } }));
  const addTimeOff = (dateStr) => {
    if (!dateStr) return;
    setForm((f) => (f.time_off.includes(dateStr) ? f : { ...f, time_off: [...f.time_off, dateStr].sort() }));
  };
  const removeTimeOff = (dateStr) => setForm((f) => ({ ...f, time_off: f.time_off.filter((d) => d !== dateStr) }));
  const addOverride = () => setForm((f) => ({ ...f, override_commissions: [...(f.override_commissions || []), { source_professional_id: "", mode: "percentage", value: 0, label: "" }] }));
  const setOverride = (idx, patch) => setForm((f) => ({ ...f, override_commissions: f.override_commissions.map((o, i) => (i === idx ? { ...o, ...patch } : o)) }));
  const removeOverride = (idx) => setForm((f) => ({ ...f, override_commissions: f.override_commissions.filter((_, i) => i !== idx) }));

  const save = async () => {
    // Normaliza campos numéricos que aceitam string durante a digitação —
    // se ficou vazio ou fora de range, envia null para o backend usar o default.
    const numRadius = form.fixed_only ? Number(form.fixed_radius_km) : null;
    const okRadius = Number.isFinite(numRadius) && numRadius >= 0.05 && numRadius <= 10;
    const numSpeed = Number(form.travel_speed_kmh);
    const okSpeed = form.travel_speed_kmh !== "" && Number.isFinite(numSpeed) && numSpeed >= 5 && numSpeed <= 120;
    const payload = {
      ...form,
      email: form.email || null,
      default_commission_value: Number(form.default_commission_value),
      fixed_radius_km: okRadius ? numRadius : (form.fixed_only ? 0.5 : null),
      travel_speed_kmh: okSpeed ? numSpeed : null,
      // Normaliza overrides: filtra os incompletos e converte valor pra número.
      override_commissions: (form.override_commissions || [])
        .filter((o) => o.source_professional_id && Number(o.value) > 0)
        .map((o) => ({
          source_professional_id: o.source_professional_id,
          mode: o.mode || "percentage",
          value: Number(o.value) || 0,
          label: (o.label || "").trim(),
        })),
    };
    // Only send access fields when the admin has filled at least the login email;
    // otherwise strip them so we don't accidentally clear an existing login.
    if (!payload.access_email) {
      delete payload.access_email;
      delete payload.access_password;
    } else if (!payload.access_password) {
      // Editing existing access without changing password: omit empty field.
      delete payload.access_password;
    }
    try {
      if (editing) await api.put(`/professionals/${editing.id}`, payload);
      else await api.post("/professionals", payload);
      toast.success("Profissional salvo");
      setOpen(false); load();
    } catch (e) {
      // Backend pode retornar detail como string OU array (validação Pydantic).
      // Tratamos ambos para nunca deixar o React quebrar tentando renderizar array.
      const raw = e?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : (Array.isArray(raw) ? raw.map((x) => x.msg || JSON.stringify(x)).join(" · ") : "Falha ao salvar");
      toast.error(msg);
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/professionals/${id}`); toast.success("Removido"); load(); }
    catch { toast.error("Falha ao remover"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Profissionais</h1>
          <p className="text-sm text-muted-foreground mt-1">Cadastre a equipe e defina comissões padrão.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-new-pro" onClick={openNew} className="h-11" disabled={!can("professionals.write")}><Plus className="h-4 w-4 mr-2" /> Novo profissional</Button></DialogTrigger>
          <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Editar profissional" : "Novo profissional"}</DialogTitle><DialogDescription>Dados do profissional, horários e permissões de acesso ao painel.</DialogDescription></DialogHeader>
            <div className="grid gap-4">
              <div className="space-y-2"><Label>Nome</Label><Input data-testid="input-pro-name" value={form.name} onChange={(e) => set("name")(e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Email</Label><Input data-testid="input-pro-email" type="email" value={form.email} onChange={(e) => set("email")(e.target.value)} /></div>
                <div className="space-y-2"><Label>Telefone</Label><Input data-testid="input-pro-phone" value={form.phone} onChange={(e) => set("phone")(e.target.value)} /></div>
              </div>
              <div className="space-y-2"><Label>Cargo/Função</Label><Input data-testid="input-pro-role" value={form.role} onChange={(e) => set("role")(e.target.value)} placeholder="Ex.: Cabeleireiro, Manicure" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de comissão padrão</Label>
                  <Select value={form.default_commission_type} onValueChange={set("default_commission_type")}>
                    <SelectTrigger data-testid="select-pro-commission-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentual (%)</SelectItem>
                      <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Valor padrão</Label><Input data-testid="input-pro-commission-value" type="number" min="0" step="0.01" value={form.default_commission_value} onChange={(e) => set("default_commission_value")(e.target.value)} /></div>
              </div>
              <div className="flex items-center justify-between">
                <Label>Ativo</Label>
                <Switch data-testid="switch-pro-active" checked={form.active} onCheckedChange={set("active")} />
              </div>

              <div className="surface-muted p-4 space-y-3">
                <div className="text-sm font-medium flex items-center gap-2"><KeyRound className="h-4 w-4" /> Acesso do profissional (opcional)</div>
                <p className="text-xs text-muted-foreground">Permite que o profissional faça login e veja apenas os próprios agendamentos. Deixe em branco para não criar acesso.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Email de acesso</Label>
                    <Input data-testid="input-pro-access-email" type="email" autoComplete="off" value={form.access_email || ""} onChange={(e) => set("access_email")(e.target.value)} placeholder="profissional@empresa.com" />
                  </div>
                  <div className="space-y-2">
                    <Label>{editing?.has_access ? "Nova senha" : "Senha"}</Label>
                    <Input data-testid="input-pro-access-password" type="password" autoComplete="new-password" value={form.access_password || ""} onChange={(e) => set("access_password")(e.target.value)} placeholder={editing?.has_access ? "Manter atual" : "Mín. 6 caracteres"} />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <div>
                    <Label>Ver agenda de todos</Label>
                    <p className="text-xs text-muted-foreground">Se desligado, o profissional vê apenas os próprios agendamentos.</p>
                  </div>
                  <Switch data-testid="switch-pro-view-all" checked={!!form.can_view_all_appointments} onCheckedChange={set("can_view_all_appointments")} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Ver comissões</Label>
                    <p className="text-xs text-muted-foreground">Se desligado, o profissional não vê a página de relatórios/comissões.</p>
                  </div>
                  <Switch data-testid="switch-pro-view-commission" checked={!!form.can_view_commission} onCheckedChange={set("can_view_commission")} />
                </div>
              </div>

              <div className="surface-muted p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Só atende no ponto fixo</Label>
                    <p className="text-xs text-muted-foreground">Quando ligado, esse profissional só aceita clientes cujo endereço bate com o endereço abaixo (raio configurável). Cliente com endereço diferente NÃO verá horários deste profissional.</p>
                  </div>
                  <Switch data-testid="switch-pro-fixed-only" checked={!!form.fixed_only} onCheckedChange={set("fixed_only")} />
                </div>
                {form.fixed_only && (
                  <>
                    <div className="space-y-2">
                      <Label>Endereço do ponto fixo</Label>
                      <Input data-testid="input-pro-fixed-address" value={form.fixed_address || ""} onChange={(e) => set("fixed_address")(e.target.value)} placeholder="Rua, número, bairro, cidade, UF" />
                    </div>
                    <div className="space-y-2">
                      <Label>Raio de tolerância (km)</Label>
                      <Input
                        data-testid="input-pro-fixed-radius"
                        type="number"
                        min="0.05"
                        max="10"
                        step="0.05"
                        value={form.fixed_radius_km ?? ""}
                        onChange={(e) => {
                          // Mantém a string enquanto o usuário digita (permite estados
                          // intermediários como "0.", "0.0") — só converte a valor
                          // final ao salvar. Sem isso, React renderiza NaN como value
                          // do input e a página quebra em branco.
                          const v = e.target.value;
                          set("fixed_radius_km")(v === "" ? "" : v);
                        }}
                        onBlur={(e) => {
                          // No blur, normaliza para número dentro do range aceito pelo
                          // backend (0.05 a 10). Fora do range → volta pro padrão 0.5.
                          const n = Number(e.target.value);
                          if (!Number.isFinite(n) || n < 0.05) set("fixed_radius_km")(0.05);
                          else if (n > 10) set("fixed_radius_km")(10);
                          else set("fixed_radius_km")(n);
                        }}
                      />
                      <p className="text-[11px] text-muted-foreground">Mínimo 0.05 km (50 m). Se o endereço do cliente cair dentro desse raio, ainda considera "no ponto fixo".</p>
                    </div>
                  </>
                )}
                {!form.fixed_only && (
                  <div className="space-y-2 pt-2 border-t border-border/50">
                    <Label>Velocidade média de deslocamento (km/h)</Label>
                    <Input
                      data-testid="input-pro-travel-speed"
                      type="number" min="5" max="120" step="5"
                      value={form.travel_speed_kmh ?? ""}
                      onChange={(e) => set("travel_speed_kmh")(e.target.value)}
                      onBlur={(e) => {
                        // Normalização no blur — assim como no raio, evita mandar
                        // string vazia ou NaN ao backend.
                        if (e.target.value === "") return; // vazio = usa default global
                        const n = Number(e.target.value);
                        if (!Number.isFinite(n) || n < 5) set("travel_speed_kmh")(5);
                        else if (n > 120) set("travel_speed_kmh")(120);
                        else set("travel_speed_kmh")(n);
                      }}
                      placeholder="35 (padrão moto urbana)"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Usada pela agenda para calcular o tempo de deslocamento entre atendimentos.
                      Ex.: 35 (moto urbana), 50 (moto em vias rápidas), 25 (carro no trânsito pesado).
                      Deixe em branco para usar o padrão (35 km/h).
                    </p>
                  </div>
                )}
              </div>

              <div className="surface-muted p-4 space-y-3">
                <div className="text-sm font-medium flex items-center gap-2">
                  <Scissors className="h-4 w-4" /> Comissão sobre outros profissionais
                </div>
                <p className="text-xs text-muted-foreground">
                  Este profissional ganha uma comissão extra em cima de cada serviço <strong>concluído</strong> de outro técnico que você escolher.
                  Só ele e o administrador enxergam esses valores no relatório.
                </p>
                {(form.override_commissions || []).length === 0 && (
                  <p className="text-xs text-muted-foreground italic">Nenhuma regra configurada.</p>
                )}
                {(form.override_commissions || []).map((o, idx) => (
                  <div key={idx} data-testid={`override-row-${idx}`} className="rounded-lg border border-border p-3 space-y-2 bg-card">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Profissional-fonte</Label>
                        <Select value={o.source_professional_id || ""} onValueChange={(v) => setOverride(idx, { source_professional_id: v })}>
                          <SelectTrigger data-testid={`override-source-${idx}`} className="h-10"><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            {list.filter((p) => p.id !== editing?.id).map((p) => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Tipo</Label>
                        <Select value={o.mode || "percentage"} onValueChange={(v) => setOverride(idx, { mode: v })}>
                          <SelectTrigger data-testid={`override-mode-${idx}`} className="h-10"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentual (%)</SelectItem>
                            <SelectItem value="fixed">Valor fixo por serviço (R$)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">{o.mode === "fixed" ? "Valor por serviço (R$)" : "Percentual sobre o valor do serviço (%)"}</Label>
                        <Input data-testid={`override-value-${idx}`} type="number" min="0" step="0.01" value={o.value ?? ""} onChange={(e) => setOverride(idx, { value: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Rótulo (opcional)</Label>
                        <Input data-testid={`override-label-${idx}`} value={o.label || ""} onChange={(e) => setOverride(idx, { label: e.target.value })} placeholder="Ex.: gerência, supervisor" />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button data-testid={`override-remove-${idx}`} type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => removeOverride(idx)}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Remover
                      </Button>
                    </div>
                  </div>
                ))}
                <Button data-testid="btn-add-override" type="button" size="sm" variant="outline" onClick={addOverride}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar regra
                </Button>
              </div>

              <div className="surface-muted p-4 space-y-3">
                <div className="text-sm font-medium flex items-center gap-2"><Clock className="h-4 w-4" /> Horário de expediente</div>
                <div className="space-y-2">
                  {DAYS.map((d) => {
                    const wh = form.working_hours[d.key] || { start: "09:00", end: "19:00", closed: false };
                    return (
                      <div key={d.key} className="flex items-center gap-2" data-testid={`wh-row-${d.key}`}>
                        <div className="w-20 text-sm">{d.label}</div>
                        <Switch data-testid={`wh-open-${d.key}`} checked={!wh.closed} onCheckedChange={(v) => setDay(d.key, { closed: !v })} />
                        {!wh.closed && (
                          <>
                            <Input data-testid={`wh-start-${d.key}`} type="time" value={wh.start} onChange={(e) => setDay(d.key, { start: e.target.value })} className="h-9 flex-1" />
                            <span className="text-xs text-muted-foreground">até</span>
                            <Input data-testid={`wh-end-${d.key}`} type="time" value={wh.end} onChange={(e) => setDay(d.key, { end: e.target.value })} className="h-9 flex-1" />
                          </>
                        )}
                        {wh.closed && <span className="text-xs text-muted-foreground flex-1">Fechado</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="surface-muted p-4 space-y-3">
                <div className="text-sm font-medium flex items-center gap-2"><CalendarX className="h-4 w-4" /> Folgas e feriados</div>
                <p className="text-xs text-muted-foreground">Datas em que o profissional não atende (bloqueia agendamento).</p>
                <div className="flex items-center gap-2">
                  <Input data-testid="input-add-time-off" type="date" className="h-10 flex-1" onChange={(e) => { if (e.target.value) { addTimeOff(e.target.value); e.target.value = ""; } }} />
                </div>
                {form.time_off.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {form.time_off.map((d) => (
                      <span key={d} data-testid={`time-off-${d}`} className="inline-flex items-center gap-1 text-xs bg-card border border-border rounded-md px-2 py-1">
                        {new Date(d + "T00:00:00").toLocaleDateString("pt-BR")}
                        <button type="button" onClick={() => removeTimeOff(d)} className="text-muted-foreground hover:text-destructive" data-testid={`btn-remove-time-off-${d}`}><XIcon className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter><Button data-testid="btn-save-pro" onClick={save}>Salvar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((p) => (
          <div key={p.id} data-testid={`pro-card-${p.id}`} className="surface p-6 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-0.5">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-accent/20 flex items-center justify-center text-accent-foreground shrink-0">
                <UserCircle2 className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground truncate">{p.role || p.email || p.phone}</div>
              </div>
              <span className={`text-xs px-2 py-1 rounded border ${p.active ? "border-primary/30 text-primary bg-primary/5" : "border-border text-muted-foreground"}`}>{p.active ? "Ativo" : "Inativo"}</span>
            </div>
            <div className="text-sm text-muted-foreground">Comissão padrão: <span className="font-medium text-foreground">{p.default_commission_type === "percentage" ? `${p.default_commission_value}%` : brl(p.default_commission_value)}</span></div>
            {p.has_access && (
              <div data-testid={`pro-access-badge-${p.id}`} className="text-xs inline-flex items-center gap-1 self-start px-2 py-0.5 rounded border border-accent/30 text-accent-foreground bg-accent/10">
                <KeyRound className="h-3 w-3" />
                {p.access_email}
                <span className="text-[10px] text-muted-foreground ml-1">
                  · {p.can_view_all_appointments ? "agenda geral" : "só própria"}
                  {p.can_view_commission ? " · comissões" : ""}
                </span>
              </div>
            )}
            <div className="flex gap-2 pt-2 border-t border-border">
              <Button data-testid={`btn-edit-pro-${p.id}`} variant="ghost" size="sm" onClick={() => openEdit(p)} disabled={!can("professionals.write")}><Pencil className="h-4 w-4 mr-1" /> Editar</Button>
              {can("professionals.delete") && (
                <AlertDialog>
                  <AlertDialogTrigger asChild><Button data-testid={`btn-delete-pro-${p.id}`} variant="ghost" size="sm" className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4 mr-1" /> Excluir</Button></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Excluir "{p.name}"?</AlertDialogTitle><AlertDialogDescription>Essa ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => remove(p.id)}>Excluir</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full surface p-12 text-center text-muted-foreground">Nenhum profissional. Cadastre o primeiro.</div>}
      </div>
    </div>
  );
}
